// De cijfers achter een grafiek als tabel: onder de grafiek in de PDF van een
// live rapportage, en straks onder het plaatje bij 'kopiëren'. Updateverzoek
// Groentjessoep (Diana), 2026-10-26.
//
// Leest de gegevens die de grafiek zelf al krijgt (MultipleData, ScaleData,
// NumberData, MatrixData) en rekent niets opnieuw uit, zodat tabel en grafiek
// nooit uiteen kunnen lopen.

/**
 * @typedef {object} ChartTable
 * @property {Array<string>} columns - Kopjes; de eerste is de rijsoort ('Antwoord', 'Meetmoment', 'Stelling')
 * @property {Array<Array<string>>} rows - Per rij de opgemaakte cellen, zelfde lengte als columns
 */

/** Teken voor 'hier is niets gemeten' — bewust geen 0, dat is een uitkomst. */
const NO_VALUE = '–';

/**
 * Zelfde precisie als de grafiek, zodat tabel en grafiek hetzelfde getal tonen:
 * de matrixgrafiek toont één decimaal (tooltip `toFixed(1)`), ScaleData en
 * NumberData geven hun gemiddelde door als `toFixed(2)`.
 */
const oneDecimal = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const twoDecimals = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {number|string|null|undefined} value
 * @param {Intl.NumberFormat} format - oneDecimal of twoDecimals, de precisie van de grafiek
 * @returns {string} Het getal met komma, of NO_VALUE als er geen getal is
 */
const formatAverage = (value, format) => {
    if (value === null || value === undefined || value === '') return NO_VALUE;
    const num = Number(value);
    return Number.isFinite(num) ? format.format(num) : NO_VALUE;
};

/**
 * Keuzevraag: per antwoord hoe vaak het per meetmoment gekozen is.
 *
 * @param {Array<{moment: string, responses: Array<{id?: string, name: string, count: number}>}>} data - Uitvoer van groupMultipleByMoment (het meetmoment heet hier `moment`, niet `name`)
 * @param {string} [firstColumn='Antwoord'] - Kop van de eerste kolom
 * @returns {ChartTable|null} null zonder gegevens
 */
export const multipleChoiceTable = (data, firstColumn = 'Antwoord') => {
    if (!Array.isArray(data) || data.length === 0) return null;

    // Opties in de volgorde waarin ze voor het eerst voorkomen; 'Anders'
    // bestaat alleen in de meetmomenten waarin iemand het koos.
    /** @type {Map<string, string>} */
    const options = new Map();
    // Zonder id (deelnemers bij een activiteit: alleen een naam, en twee
    // mensen kunnen dezelfde naam hebben) telt de plek in de lijst als sleutel.
    const keyOf = (r, index) => r.id ?? `#${index}`;
    data.forEach(moment => (moment.responses || []).forEach((r, index) => {
        const key = keyOf(r, index);
        if (!options.has(key)) options.set(key, r.name);
    }));

    const rows = [...options].map(([key, name]) => [
        name,
        ...data.map(moment => {
            const hit = (moment.responses || []).find((r, index) => keyOf(r, index) === key);
            return String(hit ? hit.count : 0);
        }),
    ]);

    return { columns: [firstColumn, ...data.map(m => m.moment)], rows };
};

/**
 * Schaal- of getalvraag: per meetmoment het gemiddelde en het aantal antwoorden.
 *
 * @param {Array<{moment: string, average: string|number, count?: number}>} groupedData - Zoals ScaleData/NumberData die opbouwen
 * @returns {ChartTable|null} null zonder gegevens
 */
export const averagesTable = (groupedData) => {
    if (!Array.isArray(groupedData) || groupedData.length === 0) return null;
    return {
        columns: ['Meetmoment', 'Gemiddelde', 'Antwoorden'],
        rows: groupedData.map(g => [
            g.moment,
            formatAverage(g.average, twoDecimals),
            g.count === undefined ? NO_VALUE : String(g.count),
        ]),
    };
};

/**
 * Matrixvraag: per stelling de gemiddelde score per meetmoment. De score van
 * een negatief geformuleerde stelling is gespiegeld; de rij zegt dat, net als
 * de tooltip van de grafiek.
 *
 * @param {Array<Object<string, any>>} data - Uitvoer van useMatrixData: `{ name, [stelling]: { value, negative } }` per meetmoment
 * @returns {ChartTable|null} null zonder gegevens
 */
export const matrixTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) return null;

    /** @type {Array<string>} */
    const statements = [];
    data.forEach(moment => Object.keys(moment).forEach(key => {
        if (key !== 'name' && !statements.includes(key)) statements.push(key);
    }));

    const rows = statements.map(title => {
        const negative = data.some(moment => moment[title]?.negative === true);
        return [
            negative ? `${title} (omgekeerd)` : title,
            ...data.map(moment => formatAverage(moment[title]?.value, oneDecimal)),
        ];
    });

    return { columns: ['Stelling', ...data.map(m => m.name)], rows };
};

/**
 * Matrixvraag als 'Verdeling': per stelling hoe vaak elk antwoord gekozen is.
 * Bij meer dan één meetmoment staat het meetmoment vooraan in elke rij.
 *
 * @param {Array<import('./matrixDistribution').MomentDistribution>} distribution - Uitvoer van useMatrixData (`distribution`)
 * @returns {ChartTable|null} null zonder gegevens
 */
export const distributionTable = (distribution) => {
    if (!Array.isArray(distribution) || distribution.length === 0) return null;

    // Categorieën in schaalvolgorde; een categorie die maar in één meetmoment
    // voorkomt krijgt in de andere 0, want daar koos niemand hem.
    /** @type {Map<string, string>} */
    const categories = new Map();
    distribution.forEach(moment => (moment.categories || []).forEach(c => {
        if (!categories.has(c.key)) categories.set(c.key, c.label);
    }));
    const keys = [...categories.keys()];
    const showMoment = distribution.length > 1;

    const rows = distribution.flatMap(moment => (moment.rows || []).map(row => [
        ...(showMoment ? [moment.moment] : []),
        row.title,
        ...keys.map(key => String(row.counts?.[key] ?? 0)),
        String(row.total),
    ]));

    return {
        columns: [...(showMoment ? ['Meetmoment'] : []), 'Stelling', ...categories.values(), 'Totaal'],
        rows,
    };
};

/**
 * @typedef {object} TableSeries
 * @property {string} key - Sleutel van de reeks in een rij (de outputtitel, of 'value')
 * @property {string} label - Kop van de kolom
 */

/**
 * Reeksen over een as (datum, status, route): per rij het aantal per reeks.
 * Voor OutputMultiLineGraph, waar outputs, activiteiten, deelnemers per
 * status/route en huisbezoeken per status doorheen gaan.
 *
 * @param {Array<Object<string, any>>} data - Rijen `{ name, [key]: { total } }`
 * @param {Array<TableSeries>} series - Eén kolom per reeks
 * @param {string} [categoryLabel='Datum'] - Kop van de eerste kolom
 * @returns {ChartTable|null} null zonder rijen of reeksen
 */
export const seriesTable = (data, series, categoryLabel = 'Datum') => {
    if (!Array.isArray(data) || data.length === 0 || !Array.isArray(series) || series.length === 0) return null;
    return {
        columns: [categoryLabel, ...series.map(s => s.label)],
        // Ontbreekt een reeks op een rij, dan tekent de grafiek 0; de tabel ook.
        rows: data.map(row => [String(row.name), ...series.map(s => String(row[s.key]?.total ?? 0))]),
    };
};

/** Uren met hoogstens twee decimalen, zoals tasksHours ze afrondt (toFixed(2)). */
const hoursFormat = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

/**
 * 'yyyy-mm-dd' → 'dd-mm-yyyy', zonder Date: geen tijdzone die een dag verschuift.
 *
 * @param {string} dayKey
 * @returns {string}
 */
const formatDayKey = (dayKey) => String(dayKey).split('-').reverse().join('-');

/**
 * Huisbezoeken, gewerkte uren: per dag de uren van die dag en het opgetelde
 * totaal (de lijn van CumulativeHoursLineChart).
 *
 * @param {Array<{day: string, hours: number, cumulativeHours: number}>} data - Uit tasksHours
 * @returns {ChartTable|null} null zonder gegevens
 */
export const hoursTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return {
        columns: ['Datum', 'Uren die dag', 'Uren totaal'],
        rows: data.map(d => [formatDayKey(d.day), hoursFormat.format(d.hours), hoursFormat.format(d.cumulativeHours)]),
    };
};

/**
 * Open vraag: per categorie hoeveel antwoorden erin vielen, per meetmoment
 * (de vorm van ParagraphData). Zelfde tabel als bij een keuzevraag.
 *
 * @param {Array<{MomentName: string, Categories: Array<{Id: string, Title: string, Words: number}>}>} data
 * @returns {ChartTable|null} null zonder gegevens
 */
export const paragraphTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return multipleChoiceTable(
        data.map(moment => ({
            moment: moment.MomentName,
            responses: (moment.Categories || []).map(c => ({ id: c.Id, name: c.Title, count: c.Words })),
        })),
        'Categorie',
    );
};

/**
 * Matrixvraag met tekstantwoorden (MatrixDevelopmentCategoricalBarChart): per
 * stelling hoe vaak elk antwoord gekozen is. Bij meer dan één meetmoment staat
 * het meetmoment vooraan, net als bij distributionTable.
 *
 * @param {Array<Object<string, any>>} data - `{ name, [stelling]: { counts: { [antwoord]: number } } }` per meetmoment
 * @returns {ChartTable|null} null zonder gegevens
 */
export const categoricalMatrixTable = (data) => {
    if (!Array.isArray(data) || data.length === 0) return null;

    /** @type {Array<string>} */
    const statements = [];
    /** @type {Array<string>} */
    const answers = [];
    data.forEach(moment => Object.keys(moment).forEach(key => {
        if (key === 'name') return;
        if (!statements.includes(key)) statements.push(key);
        Object.keys(moment[key]?.counts || {}).forEach(a => { if (!answers.includes(a)) answers.push(a); });
    }));
    const showMoment = data.length > 1;

    const rows = data.flatMap(moment => statements.map(title => {
        const counts = moment[title]?.counts || {};
        const values = answers.map(a => counts[a] || 0);
        return [
            ...(showMoment ? [moment.name] : []),
            title,
            ...values.map(String),
            String(values.reduce((sum, n) => sum + n, 0)),
        ];
    }));

    return {
        columns: [...(showMoment ? ['Meetmoment'] : []), 'Stelling', ...answers, 'Totaal'],
        rows,
    };
};

/**
 * Resultaten per week (WeeklyResultsChart), gestapeld per project. Bij één
 * project heet de kolom 'Aantal'; bij meer staat er een totaal achter.
 *
 * @param {{rows: Array<Object<string, any>>, projects: Array<string>}|null} weekly - Uit buildWeeklyProjectCounts
 * @returns {ChartTable|null} null zonder gegevens
 */
export const weeklyTable = (weekly) => {
    if (!weekly || !Array.isArray(weekly.rows) || weekly.rows.length === 0) return null;
    const projects = weekly.projects || [];
    const single = projects.length === 1;
    return {
        columns: ['Week van', ...(single ? ['Aantal'] : projects), ...(single ? [] : ['Totaal'])],
        rows: weekly.rows.map(row => {
            const values = projects.map(p => row[p] || 0);
            return [
                String(row.name),
                ...values.map(String),
                ...(single ? [] : [String(values.reduce((sum, n) => sum + n, 0))]),
            ];
        }),
    };
};

/** Een kaal getal, hoogstens twee decimalen: aantallen blijven hele getallen. */
const upToTwoDecimals = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

/**
 * Rijen met per reeks een kaal getal `{ name, [reeks]: number }`: de
 * uitsplitsing in de diepte-analyse (gemiddelde per gekozen antwoord) en de
 * gestapelde outputgrafiek. Geen waarde → streepje, want de grafiek tekent
 * daar geen staaf.
 *
 * @param {Array<Object<string, any>>} data
 * @param {Array<string>} keys - De reeksen, in de volgorde van de grafiek
 * @param {string} firstColumn - Kop van de eerste kolom
 * @returns {ChartTable|null} null zonder rijen of reeksen
 */
export const wideTable = (data, keys, firstColumn) => {
    if (!Array.isArray(data) || data.length === 0 || !Array.isArray(keys) || keys.length === 0) return null;
    return {
        columns: [firstColumn, ...keys],
        rows: data.map(row => [
            String(row.name),
            ...keys.map(key => {
                const num = Number(row[key]);
                return row[key] === null || row[key] === undefined || !Number.isFinite(num) ? NO_VALUE : upToTwoDecimals.format(num);
            }),
        ]),
    };
};

/**
 * Groei participatieladder (LadderGrowthSankey): per stroom van begintrede naar
 * huidige trede hoeveel mensen die stap maakten.
 *
 * @param {Array<{name: string}>} nodes - Treden; links verwijzen op index
 * @param {Array<{source: number, target: number, value: number}>} links
 * @returns {ChartTable|null} null zonder stromen
 */
export const ladderFlowTable = (nodes, links) => {
    if (!Array.isArray(nodes) || !Array.isArray(links) || links.length === 0) return null;
    return {
        columns: ['Begin', 'Nu', 'Aantal'],
        rows: links.map(l => [String(nodes[l.source]?.name ?? ''), String(nodes[l.target]?.name ?? ''), String(l.value)]),
    };
};

const chartDataTable = { multipleChoiceTable, averagesTable, matrixTable, distributionTable, seriesTable, hoursTable, paragraphTable, categoricalMatrixTable, weeklyTable, wideTable, ladderFlowTable };

export default chartDataTable;
